/**
 * FloorReasons / PlaysLikeReasons -- the bracket's reason lines, as the backend computed them.
 *
 * Each line is the backend's text, its bracket chip, and its evidence: card names as
 * hoverable cards, combos as "A + B — rated B4 by Commander Spellbook". No bracket policy
 * lives here (single source of truth: metacommand/deck/bracket_floor.py and plays_like.py).
 */

import { Fragment } from 'react';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { BRACKET_COLORS } from '@/lib/constants';
import { cardImageUris } from '@/lib/utils';
import type { BracketReadCallout, FloorReason, FloorReasonCombo, PlaysLikeReason } from '@/lib/api';

/** Display cap per reason; the rest collapse into "+N more". */
const MAX_COMBOS_SHOWN = 4;

export function CardName({ name }: { name: string }) {
  return (
    <span
      className="decklist-card cursor-help"
      onMouseEnter={(e) => showCardTooltip(cardImageUris(name), e)}
      onMouseLeave={hideCardTooltip}
    >
      {name}
    </span>
  );
}

function CardNames({ names, separator }: { names: string[]; separator: string }) {
  return (
    <>
      {names.map((name, i) => (
        <Fragment key={`${name}-${i}`}>
          {i > 0 && separator}
          <CardName name={name} />
        </Fragment>
      ))}
    </>
  );
}

/** One reason line: the bracket chip, the backend's text, then its cards and combos. */
function ReasonLine({ bracket, text, cards, combos }: {
  bracket: number; text: string; cards: string[]; combos: FloorReasonCombo[];
}) {
  const shown = combos.slice(0, MAX_COMBOS_SHOWN);
  const more = combos.length - shown.length;
  return (
    <li className="flex items-start gap-1.5">
      <span
        className="inline-flex items-center justify-center shrink-0 mt-[1px] px-1 rounded-[3px] text-[9px] font-bold text-white"
        style={{ background: BRACKET_COLORS[bracket] ?? 'var(--outline-variant)' }}
      >
        B{bracket}
      </span>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span>{text}</span>
        {cards.length > 0 && (
          <span className="text-on-surface">
            <CardNames names={cards} separator=", " />
          </span>
        )}
        {shown.map((c) => (
          <span key={c.id} className="text-on-surface">
            <CardNames names={c.cards} separator=" + " />
            {c.rating != null && (
              <span className="text-on-surface-variant"> — rated B{c.rating} by Commander Spellbook</span>
            )}
          </span>
        ))}
        {more > 0 && <span className="opacity-70">+{more} more</span>}
      </div>
    </li>
  );
}

export function FloorReasons({ reasons }: { reasons: FloorReason[] }) {
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-1 text-on-surface-variant" data-testid="floor-reasons">
      {reasons.map((r, i) => (
        <ReasonLine key={i} bracket={r.bracket} text={r.text} cards={r.cards} combos={r.combos} />
      ))}
    </ul>
  );
}

/** The "plays like" annotations: the same line shape; they never move the shown bracket. */
export function PlaysLikeReasons({ reasons }: { reasons: PlaysLikeReason[] }) {
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-1 text-on-surface-variant" data-testid="plays-like-reasons">
      {reasons.map((r, i) => (
        <ReasonLine key={i} bracket={r.bracket} text={r.text} cards={r.cards} combos={r.combos} />
      ))}
    </ul>
  );
}

/** The deck-read callouts against the bracket benchmark ("tutors like a B4 deck ..."): the backend's text, one line each. */
export function BracketReadCallouts({ callouts }: { callouts: BracketReadCallout[] }) {
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-1 text-on-surface-variant" data-testid="bracket-read-callouts">
      {callouts.map((c, i) => (
        <ReasonLine key={i} bracket={c.bracket} text={c.text} cards={[]} combos={[]} />
      ))}
    </ul>
  );
}
